import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Edit2, Home, Plus, RotateCcw, Trash2, User } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Card } from '../../components/ui/Card/Card';
import { Button } from '../../components/ui/Button/Button';
import { StatsStrip } from '../../components/ui/StatsStrip/StatsStrip';
import { FilterBar } from '../../components/ui/FilterBar/FilterBar';
import { Table, type Column, type RowAction } from '../../components/ui/Table/Table';
import { Pagination } from '../../components/ui/Pagination/Pagination';
import { ConfirmModal } from '../../components/ui/ConfirmModal/ConfirmModal';
import { StatusBadge } from '../../components/ui/StatusBadge/StatusBadge';
import { Checkbox } from '../../components/form/Checkbox';
import { useCustomerList } from './hooks/useCustomerList';
import { useDeleteCustomer, useRestoreCustomer } from './hooks/useCustomerMutations';
import { useAuthStore } from '../../store/authStore';
import { useDebounce } from '../../hooks/useDebounce';
import type { AccountStatus, CustomerSummary } from '../../types/api';
import type { BadgeVariant } from '../../components/ui/Badge/Badge';
import styles from './CustomersListPage.module.css';

const ACCOUNT_STATUS_MAP: Record<AccountStatus, BadgeVariant> = {
  active: 'success',
  inactive: 'warning',
  suspended: 'danger',
};

function formatMoney(n: number): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n);
}

export function CustomersListPage(): JSX.Element {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const role = useAuthStore((s) => s.user?.role ?? null);
  const isAdmin = role === 'admin';

  const q = params.get('q') ?? '';
  const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);
  const includeDeleted = params.get('deleted') === '1';

  const debouncedSearch = useDebounce(q, 300);

  const filters = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      page,
      include_deleted: includeDeleted || undefined,
    }),
    [debouncedSearch, page, includeDeleted],
  );

  const list = useCustomerList(filters);
  const deleteMutation = useDeleteCustomer();
  const restoreMutation = useRestoreCustomer();

  const [deleteTarget, setDeleteTarget] = useState<CustomerSummary | null>(null);

  function setParam(key: string, value: string | null) {
    const next = new URLSearchParams(params);
    if (value === null || value === '') next.delete(key);
    else next.set(key, value);
    if (key !== 'page') next.delete('page');
    setParams(next, { replace: false });
  }

  const customers = list.data?.customers ?? [];
  const pagination = list.data?.pagination;
  const total = pagination?.total ?? 0;
  const totalPages = pagination?.totalPages ?? 1;

  const totalJobs = useMemo(() => customers.reduce((acc, c) => acc + c.total_jobs, 0), [customers]);
  const totalSpent = useMemo(() => customers.reduce((acc, c) => acc + c.total_spent, 0), [customers]);
  const deletedCount = useMemo(() => customers.filter((c) => c.deleted_at !== null).length, [customers]);

  const columns: Column<CustomerSummary>[] = useMemo(
    () => [
      {
        key: 'customer_number',
        header: 'Customer #',
        render: (_v, row) => (
          <span className={styles.cellMono}>{row.customer_number ?? '—'}</span>
        ),
      },
      {
        key: 'full_name',
        header: 'Name',
        render: (_v, row) => (
          <div>
            <span
              className={styles.nameCell}
              onClick={() => navigate(`/customers/${encodeURIComponent(row.uuid)}`)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter') navigate(`/customers/${encodeURIComponent(row.uuid)}`);
              }}
            >
              {row.full_name ?? row.name ?? '—'}
            </span>
            {row.customer_type === 'commercial' && row.company_name ? (
              <p className={styles.companyLabel}>{row.company_name}</p>
            ) : null}
          </div>
        ),
      },
      {
        key: 'phone',
        header: 'Phone',
        render: (_v, row) => (
          <div>
            <span>{row.phone ?? '—'}</span>
            {row.phone2 ? <p className={styles.cellMuted}>{row.phone2}</p> : null}
          </div>
        ),
      },
      {
        key: 'address',
        header: 'Address',
        render: (_v, row) => (
          <div>
            <span>{row.address ?? '—'}</span>
            <p className={styles.cellMuted}>{row.postcode ?? ''}</p>
          </div>
        ),
      },
      {
        key: 'total_jobs',
        header: 'Total Jobs',
        align: 'right',
        render: (_v, row) => row.total_jobs ?? 0,
      },
      {
        key: 'total_spent',
        header: 'Total Spent',
        align: 'right',
        render: (_v, row) => formatMoney(row.total_spent ?? 0),
      },
      {
        key: 'account_status',
        header: 'Status',
        render: (_v, row) => (
          <StatusBadge
            status={row.account_status ?? null}
            statusMap={ACCOUNT_STATUS_MAP}
          />
        ),
      },
    ],
    [navigate],
  );

  const rowActions = (row: CustomerSummary): RowAction<CustomerSummary>[] => {
    const acts: RowAction<CustomerSummary>[] = [
      {
        label: 'View',
        icon: User,
        onClick: () => navigate(`/customers/${encodeURIComponent(row.uuid)}`),
      },
      {
        label: 'Edit',
        icon: Edit2,
        onClick: () => navigate(`/customers/${encodeURIComponent(row.uuid)}/edit`),
        hidden: () => row.deleted_at !== null,
      },
    ];
    if (isAdmin) {
      if (row.deleted_at !== null) {
        acts.push({
          label: 'Restore',
          icon: RotateCcw,
          variant: 'secondary',
          onClick: () => restoreMutation.mutate(row.uuid),
        });
      } else {
        acts.push({
          label: 'Delete',
          icon: Trash2,
          variant: 'danger',
          onClick: () => setDeleteTarget(row),
        });
      }
    }
    return acts;
  };

  return (
    <div className={styles.page}>
      <PageHeader
        title="Customer Management"
        subtitle="Manage and track customer information and booking history"
        actions={
          <>
            <Button
              variant="primary"
              icon={Plus}
              onClick={() => navigate('/customers/new')}
            >
              New Customer
            </Button>
            <Button
              variant="secondary"
              icon={Home}
              onClick={() => navigate('/')}
            >
              Dashboard
            </Button>
          </>
        }
      />

      <StatsStrip
        ariaLabel="Customer counters"
        tiles={[
          { key: 'total', label: 'Total Customers', value: total },
          { key: 'jobs', label: 'Total Jobs', value: totalJobs },
          { key: 'spent', label: 'Total Spent', value: formatMoney(totalSpent) },
        ]}
      />

      {isAdmin && deletedCount > 0 ? (
        <div className={styles.deletedBanner}>
          {deletedCount} deleted{' '}
          {deletedCount === 1 ? 'customer' : 'customers'} —{' '}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/customers/duplicates')}
          >
            Manage duplicates
          </Button>
        </div>
      ) : null}

      <Card flush>
        <FilterBar
          search={{
            value: q,
            onChange: (v) => setParam('q', v),
            placeholder: 'Search by name, phone, email or postcode…',
            ariaLabel: 'Search customers',
          }}
        >
          {isAdmin ? (
            <Checkbox
              label="Show deleted"
              checked={includeDeleted}
              onChange={(e) =>
                setParam('deleted', e.target.checked ? '1' : null)
              }
            />
          ) : null}
        </FilterBar>

        <Table
          columns={columns}
          data={customers}
          keyField={(row) => row.uuid}
          loading={list.isLoading}
          emptyState={
            <div className={styles.empty}>
              <h3>No customers found</h3>
              <p>
                {q
                  ? 'Try a different search term.'
                  : 'Add your first customer to get started.'}
              </p>
            </div>
          }
          rowActions={rowActions}
          onRowClick={(row) => navigate(`/customers/${encodeURIComponent(row.uuid)}`)}
          zebra
          ariaLabel="Customers"
        />

        {totalPages > 1 ? (
          <div className={styles.paginationWrap}>
            <Pagination
              pagination={{ page, limit: pagination?.limit ?? 50, total, totalPages }}
              onPageChange={(next) => setParam('page', String(next))}
            />
          </div>
        ) : null}
      </Card>

      <ConfirmModal
        open={deleteTarget !== null}
        title="Delete customer?"
        message={`Are you sure you want to delete ${deleteTarget?.full_name ?? 'this customer'}? They can be restored later.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={deleteMutation.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) {
            deleteMutation.mutate(deleteTarget.uuid, {
              onSuccess: () => setDeleteTarget(null),
            });
          }
        }}
      />
    </div>
  );
}
